import { DownOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { User, UserRole } from '@cc/schema';
import { RefineThemedLayoutV2HeaderProps, useModal } from '@refinedev/antd';
import {
  pickNotDeprecated,
  useCustomMutation,
  useGetIdentity,
  useGetLocale,
  useLogout,
  useNavigation,
  useParsed,
  useSetLocale,
  useTranslate,
} from '@refinedev/core';
import { useActor } from '@xstate/react';
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Layout as AntdLayout,
  message,
  Modal,
  Select,
  Space,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import enUSPicker from 'antd/lib/date-picker/locale/en_US';
import ruPicker from 'antd/lib/date-picker/locale/ru_RU';
import {
  formatDuration,
  intervalToDuration,
  isSameDay,
  roundToNearestMinutes,
} from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useWindowSize } from 'rooks';
import { ClientSessionTimerContext, TherapistActivityReactContext } from '../../App';
import { sessionFinishFailure } from '../../hooks/useClientSessionTimer';
import { useRefreshClientPage } from '../../pages/clients/refreshClientPage';
import { LOCAL_STORAGE_KEYS } from '../../helpers';
import { useClientRoster } from '../../hooks/client-options';
import { resources } from '../../i18n';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { IUser } from '../../providers';
import { resetCachedDbUser } from '../../providers/firebaseAuthNew';
import { PORTAL_BUILD_ID } from '../../providers/portalBuild';
import { colors } from '../../styles/colors';

import { DateFnsDatePicker } from '../Pickers';
import useStyles from './header.style';
import { ReviewSessionControl } from './ReviewSessionControl';
import { WhatsNewDrawerButton } from '../WhatsNew/WhatsNewDrawer';

const { Paragraph } = Typography;

const ThemedHeaderV2Internal: React.FC<RefineThemedLayoutV2HeaderProps> = ({
  isSticky,
  sticky,
}) => {
  const { token } = theme.useToken();

  const { innerWidth } = useWindowSize();

  const translate = useTranslate();
  const changeLanguage = useSetLocale();
  const locale = useGetLocale();
  const { styles } = useStyles();

  const currentLocale = locale();

  const { pathname } = useParsed();

  const showCLientFilter = !(
    [
      '/initial-setup',
      '/portalSubscriptions',
      '/profile/edit',
      '/practice',
    ].includes(pathname || '') || pathname?.includes('/clients/show')
  );

  const showSession = (innerWidth || 0) > 768;

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const { data: identity, remove, refetch } = useGetIdentity<IUser>();

  const { replace } = useNavigation();

  const { modalProps: modalPropsAll, show, close } = useModal();

  const { visible, ...modalProps } = modalPropsAll;

  const { mutate: logout } = useLogout();

  const { mutate } = useCustomMutation<User>();

  /**
   * E57 (Q-e57g1-2 A) — the client-session timer is the ONE shared timer of the therapist-activity context, pinned to the
   * client it was started for: this bar's Start / Finish and the client page header's drive the same clock, and Finish
   * records the session for the PINNED client whatever is picked here now (rule 1, rule 3).
   */
  const sessionTimer = useContext(ClientSessionTimerContext);
  const timer = sessionTimer?.timer ?? null;
  const isSessionActive = !!timer;
  const sessionStartTime = timer ? new Date(timer.startedAt) : undefined;
  const sessionEndTime = timer?.endedAt ? new Date(timer.endedAt) : undefined;
  const [duration, setDuration] = useState<string | undefined>();

  const refreshClientPage = useRefreshClientPage();
  const saveData = async () => {
    try {
      await sessionTimer.finish();
      refreshClientPage('session'); // a client page open now shows the new last session (review F9)
      message.success(translate('pages.sessions.saveSuccess'), 1);
    } catch (error) {
      message.error(sessionFinishFailure(error, translate('components.header.sessionSaveFailed')));
    }
  };

  useEffect(() => {
    const tick = () => {
      if (isSessionActive && sessionStartTime) {
        setDuration(
          formatDuration(
            intervalToDuration({
              start: sessionStartTime,
              end: sessionEndTime || new Date(),
            }),
            {
              format: ['days', 'hours', 'minutes'],
              locale: currentLocale?.startsWith('ru') ? ru : enUS,
            },
          ),
        );
      } else {
        setDuration(undefined);
      }
    };
    tick();
    const handle = setInterval(tick, 1000);

    return () => clearInterval(handle);
  }, [isSessionActive, timer?.startedAt, timer?.endedAt, currentLocale]);

  const updateUser = async () => {
    await mutate({
      url: '',
      method: 'post',
      values: {},
      meta: {
        operation: 'updateUserRole',
        fields: [{ user: ['_id'] }],
        variables: {
          role: {
            name: 'role',
            type: 'Int!',
            value: UserRole.THERAPIST_ADMIN,
          },
        },
      },
    });

    await remove();
    resetCachedDbUser();
    await refetch();
  };

  // the WHOLE practice roster (P011) — this is the filter every tool page reads its client from, so
  // a truncated roster made clients simply unpickable here
  const { clients, clientsLoading } = useClientRoster();

  useEffect(() => {
    // TODO: turn on when email verification is back?
    // Make sure Tamila is fine
    // if (identity?.fb && !identity?.fb?.emailVerified) {
    //   replace('/verify-email');
    // }
    const clientAccountWarningShown = localStorage.getItem(
      LOCAL_STORAGE_KEYS.clientAccountWarningShown,
    );

    if (
      identity?.db &&
      [UserRole.FREE, UserRole.PAID].includes(identity.db.role) &&
      !clientAccountWarningShown
    ) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.clientAccountWarningShown,
        'true',
      );
      show();
    }
  }, [identity?.db?.role]);

  useEffect(() => {
    if (identity?.db && !identity?.db?.practiceId) {
      // Note: it fails initially without a timeout
      const handle = setTimeout(() => replace('/initial-setup'), 50);
      return () => clearTimeout(handle);
    }
  }, [identity?.db?.practiceId]);

  const [selectedClientId, setSelectedClientId] = useState<
    string | undefined
  >();

  const clientsOptions = useMemo(() => {
    return clients.map((client) => ({
      label: `${client._id.replace('users/', '')} - ${client.firstName} ${
        client.lastName
      }`,
      value: client._id,
    }));
  }, [clients]);

  /** "412 - Maya Torres" — how the bar names a client (the roster's own label), the id alone before it loads. */
  const clientLabel = (clientId: string | null | undefined): string => {
    if (!clientId) return '';
    const c = clients.find((x) => x._id === clientId);
    return c
      ? `${clientId.replace('users/', '')} - ${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()
      : clientId.replace('users/', '');
  };

  const menuItems = Object.keys(resources)
    .sort()
    .map((lang: string) => ({
      key: lang,
      label: lang?.startsWith('en') ? 'English' : 'Русский',
      onClick: () => changeLanguage(lang),
    }));

  const headerStyles: React.CSSProperties = {
    backgroundColor: colors['--triad-blue-4'],
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0px 24px',
    height: '64px',
  };

  if (pickNotDeprecated(sticky, isSticky)) {
    headerStyles.position = 'sticky';
    headerStyles.top = 0;
    headerStyles.zIndex = 1;
  }

  return (
    <AntdLayout.Header
      //  style={headerStyles}
      className={styles.header}
      data-testid="cc-header"
    >
      <div className={styles['header-main-row']}>
        {showCLientFilter ? (
          <div
            className={styles['header-filter-client']}
            data-testid="cc-header-filter-client"
          >
            <div className={styles['header-filter-client--label']}>
              {translate('components.header.filterResults')}:
            </div>

            <Tooltip title={translate('components.header.filterTip')}>
              <QuestionCircleOutlined
                className={styles['header-filter-client--tooltip']}
              />
            </Tooltip>
            <Select
              allowClear
              loading={clientsLoading}
              options={clientsOptions}
              optionFilterProp="children"
              className={styles['header-filter-client--select']}
              mode="tags"
              tagRender={(props) => {
                const foundClient = clients.find(
                  (client) => client._id === props.value,
                );
                return foundClient ? (
                  <Tooltip
                    title={`${foundClient.firstName} ${foundClient.lastName}`}
                  >
                    {`${(selectedClientId || '').replace('users/', '')} - ${(
                      foundClient?.firstName || ''
                    ).charAt(0)}${(foundClient?.lastName || '').charAt(0)}`}
                  </Tooltip>
                ) : (
                  <></>
                );
              }}
              value={selectedClientId ? [selectedClientId] : []}
              placeholder={translate('components.header.selectClient')}
              filterOption={(input, option) =>
                ((option?.label ?? '') as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onClear={() => {
                therapistActivitySend('UNSET_FILTER_CLIENT_ID');
                setSelectedClientId(undefined);
              }}
              onChange={(value: string[]) => {
                const maybeNewValue = value?.pop();
                const foundClient = clients.find(
                  (client) => client._id === maybeNewValue,
                );
                if (foundClient) {
                  therapistActivitySend({
                    type: 'SET_FILTER_CLIENT_ID',
                    clientId: foundClient._id,
                  });
                  setSelectedClientId(foundClient._id);
                }
              }}
              showSearch
            />

            {(selectedClientId || isSessionActive) && showSession ? (
              <Tooltip
                title={
                  isSessionActive && timer?.clientId !== selectedClientId
                    ? translate('components.header.sessionPinnedTo', {
                        client: clientLabel(timer?.clientId),
                      })
                    : undefined
                }
              >
                <Button
                  type={isSessionActive ? 'primary' : 'default'}
                  loading={sessionTimer?.saving}
                  onClick={async () => {
                    if (isSessionActive) {
                      await saveData();
                    } else if (selectedClientId) {
                      sessionTimer.start(selectedClientId);
                    }
                  }}
                  className={styles['header-session-button']}
                  data-testid="cc-header-session-button"
                  data-session-client={timer?.clientId ?? ''}
                >
                  {isSessionActive
                    ? translate('components.header.finishSession')
                    : translate('components.header.startSession')}
                </Button>
              </Tooltip>
            ) : null}
            {/* E04 · the RTM review session — the second, DISTINCT session control (2026-09-21) */}
            {selectedClientId && showSession ? (
              <ReviewSessionControl
                clientId={selectedClientId}
                locale={currentLocale?.startsWith('ru') ? 'ru' : 'en'}
                className={styles['header-session-button']}
              />
            ) : null}
          </div>
        ) : null}
        {/**
         * E30 · the build chip. When the tab is fresh this is the ONLY place the running build id
         * appears — and it is what makes "what build are you on?" answerable in a support
         * conversation, which is half of why the id is a stamped file rather than a hashed bundle
         * filename (registry `version-json-vs-index-html`).
         */}
        {/* E32 · What's New — the drawer of every announcement that was ever theirs, badged with
            what is still pending */}
        <WhatsNewDrawerButton />
        <Tooltip title={translate('portalUpdate.buildChip.tooltip')}>
          <Tag data-testid="cc-build-chip">
            {translate('portalUpdate.buildChip.label', {
              buildId: PORTAL_BUILD_ID,
            })}
          </Tag>
        </Tooltip>
        <Dropdown
          className={showCLientFilter ? '' : styles['header-language-selector']}
          menu={{
            items: menuItems,
            selectable: true,
            selectedKeys: currentLocale ? [currentLocale] : [],
          }}
        >
          <Button type="link">
            <Space>
              {currentLocale?.startsWith('en') ? 'English' : 'Русский'}
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
        <Modal
          {...modalProps}
          okText={translate('modals.mobileAppAccount.okText')}
          cancelText={translate('modals.mobileAppAccount.cancelText')}
          onOk={() => {
            updateUser();
            close();
          }}
          onCancel={() => {
            logout();
          }}
          maskClosable={false}
          closable={false}
          title={translate('modals.mobileAppAccount.title')}
          data-testid="cc-mobile-app-account-modal"
        >
          <Paragraph>{translate('modals.mobileAppAccount.text1')}</Paragraph>
          <Paragraph>{translate('modals.mobileAppAccount.text2')}</Paragraph>
          <Paragraph>{translate('modals.mobileAppAccount.text3')}</Paragraph>
          <Paragraph>{translate('modals.mobileAppAccount.text4')}</Paragraph>
        </Modal>
      </div>
      {isSessionActive ? (
        <div data-testid="cc-header-session-row">
          <Divider className={styles['header-divider']} />
          <div className={styles['header-session-row']}>
            <Form.Item
              label={translate('components.header.sessionFor')}
              className={styles['header-session-row--item']}
            >
              <span data-testid="cc-header-session-client">
                {clientLabel(timer?.clientId)}
              </span>
            </Form.Item>
            <Form.Item
              label={translate('components.header.startTime')}
              className={styles['header-session-row--item']}
            >
              <DateFnsDatePicker
                locale={currentLocale?.startsWith('ru') ? ruPicker : enUSPicker}
                showTime={{
                  format: currentLocale?.startsWith('ru') ? 'HH:mm' : 'hh:mm a',
                }}
                format={
                  currentLocale?.startsWith('ru')
                    ? 'yyyy-MM-dd HH:mm'
                    : 'yyyy-MM-dd hh:mm a'
                }
                minuteStep={5}
                value={sessionStartTime}
                onChange={(value) => {
                  if (value) {
                    sessionTimer.setStartedAt(
                      roundToNearestMinutes(value, {
                        nearestTo: 1,
                        roundingMethod: 'floor',
                      }),
                    );
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              label={translate('components.header.endTime')}
              className={styles['header-session-row--item']}
            >
              <DateFnsDatePicker
                locale={currentLocale?.startsWith('ru') ? ruPicker : enUSPicker}
                showTime={{
                  format: currentLocale?.startsWith('ru') ? 'HH:mm' : 'hh:mm a',
                }}
                format={
                  currentLocale?.startsWith('ru')
                    ? 'yyyy-MM-dd HH:mm'
                    : 'yyyy-MM-dd hh:mm a'
                }
                minuteStep={5}
                value={sessionEndTime}
                disabledDate={(current) => {
                  return !!(
                    current &&
                    sessionStartTime &&
                    current < sessionStartTime
                  );
                }}
                // @ts-ignore
                disabledTime={(current) => {
                  if (current && sessionStartTime) {
                    if (isSameDay(current, sessionStartTime)) {
                      return {
                        disabledHours: () => {
                          return Array.from(
                            { length: sessionStartTime.getHours() },
                            (_, i) => i,
                          );
                        },
                        // Note: it looks like there occasionally is some bug I account fix now
                        // disabledMinutes: () => {
                        //   return Array.from(
                        //     { length: sessionStartTime.getMinutes() },
                        //     (_, i) => i,
                        //   );
                        // },
                      };
                    }
                  }
                }}
                onChange={(value) => {
                  sessionTimer.setEndedAt(
                    value
                      ? roundToNearestMinutes(value, {
                          nearestTo: 1,
                          roundingMethod: 'ceil',
                        })
                      : null,
                  );
                }}
              />
            </Form.Item>
            {duration && (
              <Form.Item
                label={translate('components.header.duration')}
                className={styles['header-session-row--item']}
              >
                {duration}
              </Form.Item>
            )}
          </div>
        </div>
      ) : null}
    </AntdLayout.Header>
  );
};

export const CcThemedHeaderV2: React.FC = () => {
  const therapistActivityService = useContext(TherapistActivityReactContext);

  return therapistActivityService ? <ThemedHeaderV2Internal /> : null;
};
